import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import * as parkingServices from "../../services/parkingServices";
import useValue from "../../hooks/useValue";
import { CustomNotification } from "../../util/CustomNotification";
import { DefaultPagination } from "../../constants/generalConstants";
import useTablePagination from "../../hooks/useTablePagination";
import { ErrorMessageConstants } from "../../constants/MessageConstants/ErrorMessageConstants";

import type { GetQueueList, ParamsObject } from "../../shared/interface/parking.interface";
import type { RootState } from "../../appRedux/store";
import type FixMeLater from "../../shared/types/fixMeLater.type";
import { useRefetchAPIShortPolling } from "../../hooks/useRefetchAPIShortPolling";

const useDisplayQueueList = () => {
  /* CONSTANTS */

  /* 10 sec short polling */
  const shortPolling = 10000;

  /* STATE */
  const [searchedText, setSearchedText] = useState<string>("");
  const [queueList, setQueueList] = useState<GetQueueList[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recordID, setRecordID] = useState<FixMeLater | null>(null);
  const [reFetch, setRefetch] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { masterFilters } = useSelector(({ common }: RootState) => common);

  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);
  const { value: saving, handleEnableValue: handleEnableSaving, handleDisableValue: handleDisableSaving } = useValue(false);

  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);
  const { shortPollingRefetch } = useRefetchAPIShortPolling(shortPolling);

  const isFirstRender = useRef(true);

  /* HANDLER */
  const startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleChangeOnSearch = (value: string) => {
    handleChangeTotalSize(queueList?.length);
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    setRefetch(true);
  };

  const handleChangeSearch = (value: string) => {
    handleChangeTotalSize(queueList?.length);
    const trimmedValue = value?.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    if (!trimmedValue) {
      setRefetch(true);
    }
  };

  const handleAddClick = () => {
    if (!masterFilters?.customerId || !masterFilters?.locationId) {
      CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
      return;
    }
    setIsModalVisible(true);
  };

  const handleOnClose = () => {
    setIsModalVisible(false);
  };

  const handleCancel = (record: FixMeLater) => {
    setRecordID(record);
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      if (!recordID?.id) return;
      handleEnableSaving();

      const res = await parkingServices.CancelQueueList(recordID?.id);
      CustomNotification.showSuccessMessage(res?.message);
      setRefetch(true);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setIsModalOpen(false);
      setRecordID(null);
      handleDisableSaving();
    }
  };

  const handleCancelModal = () => {
    setIsModalOpen(false);
    setRecordID(null);
  };

  const handleRefreshList = () => {
    handleResetPagination();
    setRefetch(true);
  };

  const fetchQueueList = async () => {
    try {
      if (reFetch) handleEnableLoading();

      const paramsObject: ParamsObject = {
        searchQuery: searchedText,
        page: pagination?.current || DefaultPagination.PAGE,
        pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId
      };

      const response = await parkingServices.getQueueList(paramsObject);

      const formattedResponse: FixMeLater = response?.data?.rows?.map((item, index) => {
        return {
          ...item,
          sr_no: startIndex + index + 1,
          full_name: item.full_name,
          user_name: `${item?.keyFobAllocation?.mobileUser?.first_name ?? ""} ${item?.keyFobAllocation?.mobileUser?.last_name ?? ""}`,
          key_FOB_token: `${item?.keyFob?.key_fob_token ?? ""} - ${item?.keyFob?.vehicleType?.vehicle_type_name ?? ""}`,
          vehicle_Type: item?.keyFob?.vehicleType?.vehicle_type_name ?? "",
          location: item?.customerLocation?.location_name ?? "",
          client: item?.customerClient?.client_name ?? "",
          queue_Status: item?.parkingStatus?.name ?? "",
          lift: item?.keyFob?.parkingMapLiftKeyFob?.lift ?? "",
          request_date: item.created_on,
          request_type_status: item?.requestType?.name ?? "",
          key_fob: item?.keyFob?.key_fob_token ?? "",
          key_fob_display_value: `${item?.keyFob?.key_fob_token ?? ""} - ${item?.keyFob?.vehicleType?.vehicle_type_name ?? ""}`
        };
      });

      handleChangeTotalSize(response?.data?.count || 0);
      setQueueList(formattedResponse);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setRefetch(false);
      handleDisableLoading();
    }
  };

  /* EFFECT */
  useEffect(() => {
    if (pagination?.current && pagination?.pageSize) {
      setRefetch(true);
    } else {
      setRefetch(false);
    }
  }, [pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId]);

  useEffect(() => {
    if (reFetch) fetchQueueList();
  }, [reFetch]);

  /* shortPollingRefetch */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchQueueList();
  }, [shortPollingRefetch]);

  return {
    loading,
    queueList,
    pagination,
    searchedText,
    setSearchedText,
    handleChangePagination,
    isModalOpen,
    recordID,
    handleAddClick,
    handleCancel,
    handleOk,
    handleCancelModal,
    handleChangeOnSearch,
    handleChangeSearch,
    handleRefreshList,
    isModalVisible,
    handleOnClose,
    saving
  };
};

export default useDisplayQueueList;
